import { Injectable, signal, computed } from '@angular/core';

export interface TourStep {
  targetSelector?: string;
  title: string;
  body: string;
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'center';
}

export interface Tour {
  id: string;
  name: string;
  steps: TourStep[];
}

export const TOURS: Record<string, Tour> = {
  dashboard: {
    id: 'dashboard', name: 'Dashboard Tour', steps: [
      { title: 'Welcome to SiteWareOS!', body: 'This is your command center — a live view of all site operations. Let\'s take a 60-second tour to get you oriented.', placement: 'center' },
      { targetSelector: '[data-tour="stat-cards"]', title: 'Live KPI Cards', body: 'These cards show your most critical metrics updated in real time — open tasks, active sites, incidents, and audit compliance scores.', placement: 'bottom' },
      { targetSelector: '[data-tour="quick-actions"]', title: 'Quick Actions', body: 'Jump to common actions without navigating menus. Create a task, log an incident, or start an audit in one click.', placement: 'top' },
      { targetSelector: '[data-tour="activity-feed"]', title: 'Live Activity Feed', body: 'See everything that\'s happening across all sites in real time — task completions, incidents logged, audits finished, and more.', placement: 'left' },
      { targetSelector: '[data-tour="nav-sidebar"]', title: 'Navigation Sidebar', body: 'Access every module here — Tasks, Sites, Incidents, Audits, CRM, and more. Click any item to dive in.', placement: 'right' },
    ]
  },
  tasks: {
    id: 'tasks', name: 'Tasks Tour', steps: [
      { title: 'Task Management', body: 'Create, assign, and track work across all your sites. Every task has a priority, assignee, site, and due date.', placement: 'center' },
      { targetSelector: '[data-tour="new-task-btn"]', title: 'Create a Task', body: 'Click here to open the task form. Set the title, site, category, priority, assignee, and due date — then save to assign it immediately.', placement: 'bottom' },
      { targetSelector: '[data-tour="view-toggle"]', title: 'List & Kanban Views', body: 'Switch between List view for detail and Kanban view for a visual board organized by status (To Do, In Progress, Overdue, Completed).', placement: 'bottom' },
      { targetSelector: '[data-tour="task-filters"]', title: 'Smart Filters', body: 'Filter by priority (High/Medium/Low) or status, and search by keyword. The clickable stat chips at the top also act as quick filters.', placement: 'bottom' },
    ]
  },
  sites: {
    id: 'sites', name: 'Sites Tour', steps: [
      { title: 'Site Management', body: 'Every physical location in your operation is a "Site". Sites connect tasks, incidents, audits, and work orders so you can see performance per location.', placement: 'center' },
      { targetSelector: '[data-tour="add-site-btn"]', title: 'Add a Site', body: 'Click here to add a new location. Enter the address, site type, manager, and other details. Once saved it appears in all site selectors across the app.', placement: 'bottom' },
      { targetSelector: '[data-tour="site-grid"]', title: 'Site Cards', body: 'Each card shows the site\'s open tasks, incidents, and last audit score at a glance. Click any card to see the full site detail view.', placement: 'top' },
    ]
  },
  incidents: {
    id: 'incidents', name: 'Incidents Tour', steps: [
      { title: 'Incident Management', body: 'Log, track, and resolve safety incidents across all sites. Every incident has a severity level, assignee, status, and comment thread.', placement: 'center' },
      { targetSelector: '[data-tour="log-incident-btn"]', title: 'Log an Incident', body: 'Click here to report a new incident. Set the title, site, severity (Critical/High/Medium/Low), category, and description. It\'s immediately visible to your team.', placement: 'bottom' },
      { targetSelector: '[data-tour="severity-filter"]', title: 'Filter by Severity', body: 'Click a severity chip to filter the list. Critical incidents are highlighted in red — always address them first.', placement: 'bottom' },
    ]
  },
  audits: {
    id: 'audits', name: 'Audits Tour', steps: [
      { title: 'Audit Management', body: 'Schedule and track compliance audits across your sites. Each audit has an assigned auditor, scheduled date, and question set.', placement: 'center' },
      { targetSelector: '[data-tour="schedule-audit-btn"]', title: 'Schedule an Audit', body: 'Click to create a new audit — select the site, type (Safety, Environmental, etc.), auditor, date, and number of questions.', placement: 'bottom' },
      { targetSelector: '[data-tour="template-builder-btn"]', title: 'Template Builder', body: 'Create reusable audit templates for different compliance types. Templates save time when scheduling repeat audits.', placement: 'bottom' },
    ]
  },
  crm: {
    id: 'crm', name: 'CRM Tour', steps: [
      { title: 'CRM & Contacts', body: 'Manage your client relationships, track opportunities, and monitor the sales pipeline — all in one place.', placement: 'center' },
      { targetSelector: '[data-tour="crm-tabs"]', title: 'Three Tabs', body: 'Contacts shows individuals, Companies shows organizations, and Pipeline shows your Kanban-style opportunity board with deal values per stage.', placement: 'bottom' },
      { targetSelector: '[data-tour="add-contact-btn"]', title: 'Add a Contact', body: 'Click to add a new contact. Fill in their name, company, role, email, and phone number — they\'ll appear in the contacts table immediately.', placement: 'bottom' },
    ]
  },
};

@Injectable({ providedIn: 'root' })
export class FeatureTourService {
  readonly activeTour = signal<Tour | null>(null);
  readonly currentStepIndex = signal(0);

  readonly currentStep = computed(() => {
    const tour = this.activeTour();
    if (!tour) return null;
    return tour.steps[this.currentStepIndex()] ?? null;
  });

  readonly totalSteps = computed(() => this.activeTour()?.steps.length ?? 0);
  readonly isLastStep = computed(() => this.currentStepIndex() >= this.totalSteps() - 1);
  readonly isFirstStep = computed(() => this.currentStepIndex() === 0);

  startTour(id: string): void {
    const tour = TOURS[id];
    if (!tour) return;
    this.activeTour.set(tour);
    this.currentStepIndex.set(0);
  }

  next(): void {
    if (this.isLastStep()) { this.end(); return; }
    this.currentStepIndex.update(i => i + 1);
  }

  prev(): void {
    if (this.isFirstStep()) return;
    this.currentStepIndex.update(i => i - 1);
  }

  end(): void {
    this.activeTour.set(null);
    this.currentStepIndex.set(0);
  }
}
